import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GTMEventCategory, GTMEventType } from '../google-tag-manager';
import { OtpStoreFactory } from '../otp/factory/otp-store.factory';
import { OtpStore } from '../otp/store/otp.store';
import { TurnstileService } from '../turnstile/turnstile.service';
import { LoginStoreFactory } from './factory/login-store.factory';
import { LoginStoreService } from './store/login.store';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', '../auth.scss'],
  providers: [
    { provide: LoginStoreFactory, useClass: LoginStoreService },
    { provide: OtpStoreFactory, useClass: OtpStore },
  ],
  animations: [
    trigger('slideCollapse', [
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden',
        })
      ),
      state(
        'collapsed',
        style({
          height: '0',
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      transition('expanded <=> collapsed', [animate('300ms ease-in-out')]),
    ]),
  ],
  standalone: false,
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  vm$ = this.loginStore.vm$;
  emailSubscription: Subscription | undefined;
  valuenya = '';
  passwordFieldType = 'password';

  readonly GTMEventType = GTMEventType;
  readonly GTMEventCategory = GTMEventCategory;

  constructor(
    private fb: FormBuilder,
    private loginStore: LoginStoreFactory,
    private otpStore: OtpStoreFactory,
    private turnstileService: TurnstileService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      isRemember: [false],
    });
  }

  ngOnInit(): void {
    // Download the Turnstile script while the user fills in the form so the
    // login request spends its time budget on the challenge, not the download
    this.turnstileService.preload();
    this.observeEmailChanges();
    this.loginStore.checkAccessToken();
  }

  observeEmailChanges() {
    this.emailSubscription = this.loginForm.get('email')?.valueChanges.subscribe(email => {
      if (this.loginForm.get('email')?.valid) {
        this.checkLocalStorageForFingerprint(email);
      } else {
        // Reset checkbox and device recognition state when email is invalid
        this.loginForm.get('isRemember')?.setValue(false);
        this.loginStore.resetDeviceRecognition();
      }
    });
  }

  checkLocalStorageForFingerprint(email: string) {
    const storedFingerprint = this.loginStore.getFingerPrintId(email);
    if (storedFingerprint) {
      this.loginForm.get('isRemember')?.setValue(true);
    } else {
      this.loginForm.get('isRemember')?.setValue(false);
    }
    this.loginStore.checkStoredFingerprint(email);
  }

  onSubmit() {
    const { email, password, isRemember } = this.loginForm.value;

    // Check if there's an existing OTP session for this email
    const hasActiveSession = this.otpStore.hasActiveSession();

    if (hasActiveSession) {
      const sessionDataStr = localStorage.getItem('otpSession');

      if (sessionDataStr) {
        const sessionData = JSON.parse(sessionDataStr);

        if (sessionData.email === email) {
          // Resume existing session
          this.otpStore.loadOtpSession();
          this.loginStore.resumeOtpSession();
          return;
        }
      }
    }

    // Proceed with normal login
    this.loginStore.login({ email, password, isRemember });
  }

  disableOtpRequire() {
    this.loginStore.disableOtpRequire();
  }

  togglePasswordType() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  /**
   * Get the appropriate eye icon name for password field
   */
  get passwordEyeIconName(): string {
    return this.passwordFieldType === 'password' ? 'eye' : 'eye-off';
  }
}
